import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {ErrorDisplayService} from '../../../basic-entity-front/services/error-display.service';
import {ConversacionesService} from '../conversaciones.service';
import {AdjuntoConversacion, ComunicadoResumen, esImagen, tamanoLegible} from '../conversaciones.model';

/**
 * Detalle de un comunicado de CNC Dental a varios clientes a la vez: lo usan la bandeja de mensajes
 * (con el enlace a «abre una consulta») y el diálogo de aviso al entrar (sin ese enlace, vía
 * `conEnlaceConsulta`). Las miniaturas de los adjuntos imagen siguen el mismo patrón que
 * HiloConversacionComponent: blob -> object URL -> bypassSecurityTrustUrl, liberadas al destruir o
 * al cambiar de comunicado.
 */
@Component({
    selector: 'app-comunicado-detalle',
    templateUrl: './comunicado-detalle.component.html',
    styleUrls: ['./comunicado-detalle.component.scss']
})
export class ComunicadoDetalleComponent implements OnChanges, OnDestroy {
    @Input() comunicado: ComunicadoResumen | null = null;
    @Input() conEnlaceConsulta = true;
    @Output() abrirConsulta = new EventEmitter<void>();

    miniaturas: Record<number, string> = {};
    /** URLs de las miniaturas ya autorizadas para el `[src]` de la plantilla (ver _cargarMiniaturas). */
    miniaturasSeguras: Record<number, SafeUrl> = {};
    readonly esImagen = esImagen;
    readonly tamano = tamanoLegible;

    constructor(private _conversaciones: ConversacionesService, private _errores: ErrorDisplayService, private _sanitizer: DomSanitizer) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['comunicado']) {
            this._liberarMiniaturas();
            this._cargarMiniaturas();
        }
    }

    ngOnDestroy(): void {
        this._liberarMiniaturas();
    }

    descargar(adjunto: AdjuntoConversacion): void {
        this._conversaciones.descargarAdjuntoComunicado(adjunto).subscribe({error: e => this._errores.displayError(e)});
    }

    ampliar(adjunto: AdjuntoConversacion): void {
        if (this.miniaturas[adjunto.id]) {
            window.open(this.miniaturas[adjunto.id], '_blank');
        }
    }

    private _cargarMiniaturas(): void {
        for (const adjunto of this.comunicado?.adjuntos || []) {
            if (esImagen(adjunto)) {
                this.miniaturas[adjunto.id] = '';
                this._conversaciones.blobAdjuntoComunicado(adjunto.id).subscribe({
                    next: blob => {
                        const url = URL.createObjectURL(blob);
                        this.miniaturas[adjunto.id] = url;
                        // bypassSecurityTrustUrl es seguro aquí porque la URL blob: la genera este mismo
                        // componente con URL.createObjectURL a partir del blob descargado de la API; no es
                        // una URL ajena ni construida con datos externos (igual que en HiloConversacionComponent).
                        this.miniaturasSeguras[adjunto.id] = this._sanitizer.bypassSecurityTrustUrl(url);
                    },
                    error: () => delete this.miniaturas[adjunto.id]
                });
            }
        }
    }

    private _liberarMiniaturas(): void {
        Object.values(this.miniaturas).filter(u => !!u).forEach(u => URL.revokeObjectURL(u));
        this.miniaturas = {};
        this.miniaturasSeguras = {};
    }
}
